import React from 'react';
import { useTheme } from '../context/ThemeContext';
import './AuthPages.scss';

const PrivacyPolicyPage = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div className={`auth-page ${isDark ? 'auth-page--dark' : 'auth-page--light'}`}>
      <div className="policy-page">
        <div className="policy-page__content">
          <h1 className="policy-page__title">Privacy Policy</h1>
          <p className="policy-page__updated"><strong>Last Updated:</strong> December 29, 2025</p>
          
          <p className="policy-page__intro">
            Chill Verify ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and services.
          </p>

          <section className="policy-page__section">
            <h2>1. Information We Collect</h2>
            
            <h3>1.1 Personal Information</h3>
            <ul>
              <li>Email address and account credentials</li>
              <li>Name and contact information</li>
              <li>Payment information (processed securely through Stripe)</li>
              <li>Device identifiers and serial numbers</li>
            </ul>

            <h3>1.2 Sensor Data</h3>
            <ul>
              <li>Temperature and humidity readings from connected sensors</li>
              <li>Battery levels and device status information</li>
              <li>Timestamp and location data associated with readings</li>
              <li>Historical sensor data and trends</li>
            </ul>

            <h3>1.3 Location Data</h3>
            <p>We collect location information when you:</p>
            <ul>
              <li>Create or update zones on the map</li>
              <li>Track trips for mobile zones</li>
              <li>Attach address information to zones</li>
            </ul>
            <p>Location data is used solely for zone management and trip tracking features. You can disable location permissions in your device settings at any time.</p>

            <h3>1.4 Device Permissions</h3>
            <p>Our app requires the following permissions to function:</p>
            <ul>
              <li><strong>Bluetooth:</strong> To connect to and communicate with temperature/humidity sensors</li>
              <li><strong>Location:</strong> To display zones on maps and track mobile zones</li>
              <li><strong>Camera:</strong> To scan QR codes for sensor setup</li>
              <li><strong>Notifications:</strong> To send alerts about temperature/humidity thresholds</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>2. How We Use Your Information</h2>
            <ul>
              <li>Provide and maintain our sensor monitoring services</li>
              <li>Process transactions and manage your orders</li>
              <li>Send you alerts and notifications based on sensor readings</li>
              <li>Display sensor data, charts, and historical trends</li>
              <li>Manage zones, trips, and device configurations</li>
              <li>Improve our services and develop new features</li>
              <li>Respond to your inquiries and provide customer support</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>3. Data Storage and Security</h2>
            <p>Your data is stored securely using:</p>
            <ul>
              <li><strong>Supabase:</strong> Cloud database for sensor readings, user accounts, and configuration data</li>
              <li><strong>Local SQLite:</strong> On-device storage for offline access and faster performance</li>
              <li><strong>Encryption:</strong> Data is encrypted in transit and at rest</li>
              <li><strong>Secure Authentication:</strong> Industry-standard authentication and authorization</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>4. Third-Party Services</h2>
            <p>We use the following third-party services:</p>
            <ul>
              <li><strong>Supabase:</strong> Backend database and authentication services</li>
              <li><strong>Stripe:</strong> Payment processing (we do not store your full payment card details)</li>
              <li><strong>Twilio:</strong> SMS alert notifications (phone numbers are only used for alerts)</li>
              <li><strong>Mapbox:</strong> Map display and geocoding services</li>
              <li><strong>Expo:</strong> App development and deployment platform</li>
            </ul>
            <p>These services have their own privacy policies. We encourage you to review them.</p>
          </section>

          <section className="policy-page__section">
            <h2>5. Data Sharing and Disclosure</h2>
            <p>We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:</p>
            <ul>
              <li>With your explicit consent</li>
              <li>To comply with legal obligations or court orders</li>
              <li>To protect our rights, privacy, safety, or property</li>
              <li>In connection with a business transfer or merger</li>
              <li>With service providers who assist in operating our services (under strict confidentiality agreements)</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>6. Your Rights and Choices</h2>
            <p>You have the right to:</p>
            <ul>
              <li>Access, update, or delete your personal information</li>
              <li>Opt-out of non-essential communications</li>
              <li>Disable location, Bluetooth, or camera permissions in device settings</li>
              <li>Delete your account and associated data</li>
              <li>Request a copy of your data</li>
              <li>Withdraw consent for data processing (where applicable)</li>
            </ul>
            <p>To exercise these rights, please contact us at <a href="mailto:privacy@chillverify.com">privacy@chillverify.com</a>.</p>
          </section>

          <section className="policy-page__section">
            <h2>7. Data Retention</h2>
            <p>We retain your data for as long as necessary to provide our services and comply with legal obligations. Sensor readings are retained according to your configured retention settings (default: 90 days). You can adjust retention periods in your account settings.</p>
          </section>

          <section className="policy-page__section">
            <h2>8. Children's Privacy</h2>
            <p>Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.</p>
          </section>

          <section className="policy-page__section">
            <h2>9. International Data Transfers</h2>
            <p>Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. By using our services, you consent to the transfer of your information to these countries.</p>
          </section>

          <section className="policy-page__section">
            <h2>10. Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.</p>
          </section>

          <section className="policy-page__section">
            <h2>11. Contact Us</h2>
            <p>If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
            <ul>
              <li><strong>Email:</strong> <a href="mailto:privacy@chillverify.com">privacy@chillverify.com</a></li>
              <li><strong>Website:</strong> <a href="https://chillverify.com" target="_blank" rel="noopener noreferrer">https://chillverify.com</a></li>
            </ul>
            <p><strong>Note:</strong> This privacy policy applies to the Chill Verify mobile application and web services. By using our services, you acknowledge that you have read and understood this Privacy Policy.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
